import { of, from, forkJoin, merge, combineLatest } from 'rxjs';
import moment from 'moment';
import {
  distinctUntilChanged,
  catchError,
  filter,
  map,
  flatMap,
  switchMap,
  take,
  takeUntil,
  tap,
  debounceTime,
} from 'rxjs/operators';
import {
  InConfigurationAction,
  InConfigurationActionTypes,
  InConfigurationState,
} from '../../types/in-configuration';
import { Capture, Organization, Note, NoteAction } from '../../types/api';
import {
  RESOURCE_IDLE,
  RESOURCE_LOADING,
  ResourceStatus,
} from '../../types/resource';
import { SORT_EMPTY } from '../../types/sort';
import {
  serializeListViewState,
  updateQueryString,
} from '../../utils/rx-store-serialization';
import downloadCsv from '../../utils/download-csv';
import {
  generateParams,
  fetchInitialSensors,
  fetchNextPageOfSensors,
} from '../../rx-actions/load-in-configuration';

import createRxStore, { actions, ofType, rxDispatch } from '..';
import SessionStore from '../session';
import { SessionState, SessionStatus } from '../../types/session';
import {
  Sensor,
  CaptureStatus,
  CAPTURE_STATUS_TO_PRETTY_NAME,
} from '../../types/api';
import { SenseiApi } from '../../utils/api';
import { getRequiredSessionToken } from '../../utils/session-token';
import { Toaster } from '@densityco/ui';

const formatCsvDate = (date: string | null) => {
  if (!date) {
    return '';
  } else {
    return moment.utc(date).format('YYYY-MM-DD');
  }
};

const CSV_EXPORT_COLUMNS: Array<[string, (i: Sensor) => string | number]> = [
  ['Serial Number', (row) => row.serial_number],
  ['Organization Name', (row) => row.organization_name],
  ['Current Status', (row) => row.current_status],

  ['Organization Disposition', (row) => row.organization_disposition],
  ['Sensor Type', (row) => row.sensor_type],
  ['Created At', (row) => formatCsvDate(row.created_at)],
  ['Installed At', (row) => formatCsvDate(row.latest_install_date)],
  ['First Heartbeat', (row) => formatCsvDate(row.first_heartbeat)],
  [
    'First Heartbeat After Install',
    (row) => formatCsvDate(row.first_heartbeat_after_latest_install_date),
  ],
  ['OS Version', (row) => row.os_version],
  ['In MPI', (row) => (row.in_mpi ? 'TRUE' : 'FALSE')],
  ['Total Accuracy', (row) => row.latest_report.total_accuracy],
  ['Ingress Accuracy', (row) => row.latest_report.ingresses_annotated],
  ['Egress Accuracy', (row) => row.latest_report.egresses_annotated],
  ['Annotated Events', (row) => row.installation.total_annotated_events],
  ['Latest Report', (row) => row.latest_report.report_name],
  ...Object.keys(CaptureStatus).map(
    (status) =>
      [
        `Captures ${CAPTURE_STATUS_TO_PRETTY_NAME[status][0]}`,
        (row) => row[`captures_${status}`],
      ] as [string, (i: Sensor) => number],
  ),
];

export const initialState: InConfigurationState = {
  selectedSensorSerialNumbers: [],
  filters: [],
  searchText: '',
  sort: SORT_EMPTY,

  organizations: [],

  page: 1,

  resource: RESOURCE_IDLE,
};

function reducer(
  state: InConfigurationState,
  action: InConfigurationAction,
): InConfigurationState {
  switch (action.type) {
    // =================================
    // NAVIGATE TO LIST VIEW
    // =================================
    case InConfigurationActionTypes.IN_CONFIGURATION_CHANGE_PAGE:
      return {
        ...state,
        selectedSensorSerialNumbers: state.selectedSensorSerialNumbers,
        organizations: state.organizations,

        filters: action.filters,
        sort: action.sort,
        searchText: action.searchText,

        page: 1,

        resource: RESOURCE_IDLE,
      };

    case InConfigurationActionTypes.IN_CONFIGURATION_LOAD_COMPLETE:
      return {
        ...state,
        selectedSensorSerialNumbers: state.selectedSensorSerialNumbers,
        searchText: action.searchText,
        sort: action.sort,
        filters: action.filters,

        organizations: action.organizations,

        page: 1,

        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            sensors: action.sensors,
            total: action.total,
            fetchingMoreSensors: RESOURCE_IDLE,
            csvExport: { status: 'IDLE' as const },
            sensorStateChoices: action.sensorStateChoices,
            sensorStateHumanReadableNameMapping:
              action.sensorStateHumanReadableNameMapping,
          },
        },
      };

    case InConfigurationActionTypes.IN_CONFIGURATION_LOAD_START:
      return {
        ...state,
        resource: RESOURCE_LOADING,
      };
    case InConfigurationActionTypes.IN_CONFIGURATION_LOAD_ERROR:
      return {
        ...state,
        resource: { status: ResourceStatus.ERROR, error: action.error },
      };
  }

  if (state.resource.status !== ResourceStatus.COMPLETE) {
    return state;
  }

  switch (action.type) {
    case InConfigurationActionTypes.IN_CONFIGURATION_SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.text,
      };

    case InConfigurationActionTypes.IN_CONFIGURATION_SET_SORT:
      return {
        ...state,
        sort: { column: action.column, direction: action.direction },
      };

    case InConfigurationActionTypes.IN_CONFIGURATION_SET_FILTERS:
      return {
        ...state,
        filters: action.filters,
        selectedSensorSerialNumbers: [],
      };

    case InConfigurationActionTypes.IN_CONFIGURATION_CHANGE_CHECKS:
      return {
        ...state,
        selectedSensorSerialNumbers: action.checked
          ? Array.from(
              new Set([
                ...state.selectedSensorSerialNumbers,
                ...action.serialNumbers,
              ]),
            )
          : state.selectedSensorSerialNumbers.filter(
              (sn) => !action.serialNumbers.includes(sn),
            ),
      };

    case InConfigurationActionTypes.IN_CONFIGURATION_SET_PAGE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreSensors: RESOURCE_LOADING,
          },
        },
      };

    case InConfigurationActionTypes.IN_CONFIGURATION_NEXT_PAGE_LOAD_ERROR:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreSensors: {
              status: ResourceStatus.ERROR,
              error: action.error,
            },
          },
        },
      };

    case InConfigurationActionTypes.IN_CONFIGURATION_NEXT_PAGE_LOAD_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            sensors: [...state.resource.data.sensors, ...action.sensors],
            fetchingMoreSensors: RESOURCE_IDLE,
          },
        },
      };

    // When the CSV export button is clicked, transition into the initial csv export state
    case InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            csvExport: { status: 'LOADING_TOTAL' as const },
          },
        },
      };

    // When the CSV export process determines the total number of rows to fetch
    case InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_TOTAL_LOADED:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            csvExport: {
              status: 'LOADING_DATA' as const,
              totalRows: action.totalRows,
              loadedRows: action.initiallyLoadedRows,
            },
          },
        },
      };

    // When the CSV export process loads a new page of data
    case InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_DATA_PAGE_LOADED:
      if (state.resource.data.csvExport.status !== 'LOADING_DATA') {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            csvExport: {
              status: 'LOADING_DATA' as const,
              totalRows: state.resource.data.csvExport.totalRows,
              loadedRows:
                state.resource.data.csvExport.loadedRows + action.pageLength,
            },
          },
        },
      };

    // When the CSV export process completes / is cancelled, reset the state.
    case InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_COMPLETE:
    case InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_CANCEL:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            csvExport: { status: 'IDLE' as const },
          },
        },
      };

    case InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_ERROR:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            csvExport: { status: 'ERROR' as const, error: action.error },
          },
        },
      };

    default:
      return state;
  }
}

const InConfigurationStore = createRxStore(
  'InConfigurationStore',
  initialState,
  reducer,
);

// =================================
// SIDE EFFECT: Load first page of sensor data when one of these occurs:
// 1. Page is initially loaded (possibly with a new organization, so extract the organization from the action)
// 3. Search text is updated (has a debounce in it)
// 3. Filters or sort are updated
// =================================
const initialLoadStream = actions.pipe(
  ofType<{ organizationId: Organization['id'] | null }>(
    InConfigurationActionTypes.IN_CONFIGURATION_CHANGE_PAGE,
  ),
  switchMap(
    (action) =>
      combineLatest(
        InConfigurationStore.pipe(take(1)),
        SessionStore.pipe(take(1)),
      ),
    (action, [inConfigurationState, sessionState]) => ({
      inConfigurationState,
      sessionState,
      organizationId: action.organizationId,
    }),
  ),
);

const searchTextUpdateStream = actions.pipe(
  ofType(InConfigurationActionTypes.IN_CONFIGURATION_SET_SEARCH_TEXT),
  debounceTime(1000),
  switchMap(
    () =>
      combineLatest(
        InConfigurationStore.pipe(take(1)),
        SessionStore.pipe(take(1)),
      ),
    (action, [inConfigurationState, sessionState]) => ({
      inConfigurationState,
      sessionState,
      organizationId:
        sessionState.status === SessionStatus.LOGGED_IN
          ? sessionState.selectedOrganizationId
          : null,
    }),
  ),
);

const sortFilterUpdateStream = actions.pipe(
  ofType(
    InConfigurationActionTypes.IN_CONFIGURATION_SET_FILTERS,
    InConfigurationActionTypes.IN_CONFIGURATION_SET_SORT,
  ),
  switchMap(
    () =>
      combineLatest(
        InConfigurationStore.pipe(take(1)),
        SessionStore.pipe(take(1)),
      ),
    (action, [inConfigurationState, sessionState]) => ({
      inConfigurationState,
      sessionState,
      organizationId:
        sessionState.status === SessionStatus.LOGGED_IN
          ? sessionState.selectedOrganizationId
          : null,
    }),
  ),
);

merge(
  initialLoadStream,
  searchTextUpdateStream,
  sortFilterUpdateStream,
).subscribe(({ inConfigurationState, sessionState, organizationId }) => {
  if (sessionState.status !== SessionStatus.LOGGED_IN) {
    return;
  }

  fetchInitialSensors(
    rxDispatch,
    sessionState.token,
    inConfigurationState.filters,
    inConfigurationState.sort,
    inConfigurationState.searchText,
    organizationId,
  );
});

// =================================
// SIDE EFFECT: When the filters / search / sort updates, update the query parameters
// =================================
actions
  .pipe(
    ofType(
      InConfigurationActionTypes.IN_CONFIGURATION_SET_FILTERS,
      InConfigurationActionTypes.IN_CONFIGURATION_SET_SEARCH_TEXT,
      InConfigurationActionTypes.IN_CONFIGURATION_SET_SORT,
    ),
    debounceTime(500),
    switchMap(() => InConfigurationStore.pipe(take(1))),
  )
  .subscribe((state) => {
    const params = serializeListViewState(
      state.filters,
      state.searchText,
      state.sort,
    );
    updateQueryString(params);
  });

// =================================
// SIDE EFFECT: when a user reaches the end of the spaces list page, load more data
// =================================
actions
  .pipe(
    ofType(InConfigurationActionTypes.IN_CONFIGURATION_SET_PAGE),
    switchMap(
      () =>
        combineLatest(
          InConfigurationStore.pipe(take(1)),
          SessionStore.pipe(take(1)),
        ),
      (action, [inConfigurationState, sessionState]) => ({
        action,
        inConfigurationState,
        sessionState,
      }),
    ),
  )
  .subscribe(({ action, inConfigurationState, sessionState }) => {
    if (sessionState.status !== SessionStatus.LOGGED_IN) {
      return;
    }
    if (inConfigurationState.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    const a = action as any;

    fetchNextPageOfSensors(
      rxDispatch,
      sessionState.token,
      inConfigurationState.filters,
      inConfigurationState.sort,
      inConfigurationState.searchText,
      a.value,
      sessionState.selectedOrganizationId,
      inConfigurationState.resource.data.sensorStateHumanReadableNameMapping,
    );
  });

// =================================
// SIDE EFFECT: when a user clicks on a note action shown in a sensor row, perform the requisite
// operation
// =================================
actions
  .pipe(
    ofType<{
      serialNumber: Sensor['serial_number'];
      noteId: Note['id'];
      noteAction: NoteAction;
    }>(InConfigurationActionTypes.IN_CONFIGURATION_CLICK_NOTE_ACTION),
    switchMap(
      () => InConfigurationStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();

    // Non server based note actions shouldn't cause a request to go to the server
    if (action.noteAction.type !== 'server') {
      return;
    }

    SenseiApi.performNoteAction(
      jwtToken,
      action.serialNumber,
      action.noteId,
      action.noteAction.id,
    )
      .then((note) => {
        Toaster.showToast({ text: 'Performed note action.' });
      })
      .catch((err) =>
        Toaster.showToast({
          text: 'Error performing note action!',
          type: 'error',
        }),
      );
  });

// =================================
// SIDE EFFECT: when a user clicks "export csv", query all pages of data and generate a CSV to
// download
// =================================
const CSV_EXPORT_PAGE_SIZE = 300;
actions
  .pipe(
    ofType(InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT),
    switchMap(
      () =>
        forkJoin(
          InConfigurationStore.pipe(take(1)),
          SessionStore.pipe(take(1)),
        ),
      (action, [state, session]) => ({ state, session, action }),
    ),

    switchMap(
      ({
        state,
        session,
        action,
      }: {
        state: InConfigurationState;
        session: SessionState;
        action: { organizationId: Organization['id'] | null };
      }) => {
        if (session.status !== SessionStatus.LOGGED_IN) {
          return;
        }
        if (state.resource.status !== ResourceStatus.COMPLETE) {
          return;
        }

        const jwtToken = session.token;

        const params = generateParams(
          state.filters,
          state.searchText,
          state.sort,
          action.organizationId,
          state.resource.data.sensorStateHumanReadableNameMapping,
        );

        const initialPageResponse = SenseiApi.sensorList(jwtToken, {
          ...params,
          page: 1,
          page_size: CSV_EXPORT_PAGE_SIZE,
        });

        // Wait for the first page of data to come back from the server
        return from(initialPageResponse).pipe(
          catchError((error) => {
            rxDispatch({
              type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_ERROR,
              error,
            });
            return from([]);
          }),
          tap((responseBody) => {
            rxDispatch({
              type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_TOTAL_LOADED,
              totalRows: responseBody.total,
              initiallyLoadedRows: responseBody.results.length,
            });
          }),

          // Using the "total" value returned in the first page, figure out the number of subsequent pages
          // of data that exist, and fetch them all.
          switchMap((responseBody) => {
            let pagesToFetch = Math.ceil(
              responseBody.total / CSV_EXPORT_PAGE_SIZE,
            );
            let pagesOfData = [of(responseBody.results)];

            for (let page = 2; page <= pagesToFetch; page += 1) {
              pagesOfData.push(
                from(
                  SenseiApi.sensorList(jwtToken, {
                    ...params,
                    page,
                    page_size: CSV_EXPORT_PAGE_SIZE,
                  }),
                ).pipe(
                  catchError((error) => {
                    rxDispatch({
                      type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_ERROR,
                      error,
                    });
                    return from([]);
                  }),
                  tap((responseBody) => {
                    rxDispatch({
                      type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_DATA_PAGE_LOADED,
                      pageLength: responseBody.results.length,
                    });
                  }),
                  map((responseBody) => responseBody.results),
                ),
              );
            }

            return forkJoin(...pagesOfData);
          }),

          // If the CSV export is cancelled, then don't actually download the file at the end
          takeUntil(
            actions.pipe(
              ofType(
                InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_CANCEL,
              ),
            ),
          ),
        );
      },
    ),

    // Join all pages of data into one big list
    map((pagesOfData) => pagesOfData.flat()),
  )
  .subscribe((data) => {
    rxDispatch({
      type: InConfigurationActionTypes.IN_CONFIGURATION_CSV_EXPORT_COMPLETE,
    });

    const headers = CSV_EXPORT_COLUMNS.map((i) => `"${i[0]}"`).join(',');
    const rows = data
      .map((row) =>
        CSV_EXPORT_COLUMNS.map(([name, generator]) => {
          const data = generator(row);
          let result = `${data === null ? '' : data}`
            .replace(/"/g, '""')
            .replace(/^=/, '');
          return `"${result}"`;
        }),
      )
      .join('\n');

    downloadCsv(
      `sensors ${moment().format('YYYY-MM-DD hh:mm')}.csv`,
      `${headers}\n${rows}`,
    );
  });

export default InConfigurationStore;
